import {
  isOrganizationInSiteScores,
  loadSessionStoredValue,
  NoPaddedRow,
  saveSessionStoredValue,
  TableListSortFilters,
} from "@inrange/theme-components";
import { TenantSiteListMetricCards } from "@inrange/theme-components/site";
import {
  Table,
  TenantSiteHeader,
  TenantSiteRow,
} from "@inrange/theme-components/table";
import React, { useEffect, useState } from "react";
import {
  filterSitesByText,
  sortAscendingForColumn,
  sortByColumn,
  togglesortAscending,
} from "./SiteListSortMethods";

import {
  Organisation,
  OrgSiteListEntry,
} from "@inrange/building-manager-api-client/models-organisation";
import {
  calcAverageTariff,
  calcEmissionsAvoided,
  calcSummaryCurrencyCode,
  calcTotalAnnualDemand,
  calcTotalEnergyProcuredCost,
  calcTotalSavings,
  isAnySitePvLease,
} from "../routes/dashboard/aggregationCalculations";
import { sortOwnershipSelectOptions } from "./sitelist-utils";

const calcLandlordFrequency = (
  sites: OrgSiteListEntry[]
): Record<string, number> => {
  const landlords: Record<string, number> = {};
  for (const site of sites) {
    // Set a baseline of -1 (for sites with an unknown landlord)
    site.landlordCount = -1;

    for (const siteOwnership of site.siteOwnerships.filter(
      (so) => !so.isTest
    )) {
      if (siteOwnership.ownership === "landlord") {
        if (landlords[siteOwnership.name!]) {
          landlords[siteOwnership.name!] += 1;
        } else {
          landlords[siteOwnership.name!] = 1;
        }
      }
      if (siteOwnership.ownership === "ownerOccupier") {
        site.landlordCount = 0;
      }
    }
  }
  // Iterate back through the list to set the landlord count
  for (const site of sites) {
    for (const siteOwnership of site.siteOwnerships.filter(
      (so) => !so.isTest
    )) {
      if (siteOwnership.ownership === "landlord") {
        site.landlordCount = landlords[siteOwnership.name!];
      }
    }
  }

  return landlords;
};

interface TenantSiteListViewProps {
  sites: OrgSiteListEntry[];
  organisation: Organisation;
  landlordFilteringObject: Record<string, { name: string; siteIDs: string[] }>;
  onLandlordFiltering: (filterAction: string, filterOrgNames: string[]) => void;
}

const TenantSiteListView: React.FC<TenantSiteListViewProps> = ({
  sites,
  organisation,
  landlordFilteringObject,
  onLandlordFiltering,
}) => {
  const [textFilter, setTextFilter] = useState<string>("");
  const [selectedLandlordOrgIDs, setSelectedLandlordOrgIDs] = useState<
    string[]
  >(() => {
    if (!landlordFilteringObject) {
      return [];
    }
    const storedValue = loadSessionStoredValue(organisation.id, "landlord");
    return storedValue ? (storedValue as string[]) : [];
  });

  const orgID = organisation.id;
  const displayInRangeScoreCol = isOrganizationInSiteScores(orgID, sites);
  const allSitesNotStarted = sites.every(
    (site) => site.operationalStatus === "notStarted"
  );
  const defaultSortColumn =
    displayInRangeScoreCol || !allSitesNotStarted
      ? "siteStatusTenant"
      : "networkAvailabilityTenant";
  const [sortColumn, setSortColumn] = useState<string>(defaultSortColumn);
  const [sortAscending, setSortAscending] = useState<boolean>(
    sortAscendingForColumn(defaultSortColumn)
  );
  sortByColumn(sites, sortColumn, sortAscending, organisation); // Initial sort
  const [filteredSites, setFilteredSites] = useState<OrgSiteListEntry[]>(sites);

  // Required metrics
  const siteCount = filteredSites.length;
  const summaryCurrencyCode = calcSummaryCurrencyCode(filteredSites);
  const totalAnnualDemand = calcTotalAnnualDemand(filteredSites);
  const totalEnergyProcuredCost = calcTotalEnergyProcuredCost(
    filteredSites,
    summaryCurrencyCode
  );
  const emissionsAvoided = calcEmissionsAvoided(filteredSites);
  const totalPpaSavings = calcTotalSavings(filteredSites, summaryCurrencyCode);
  const averageTariff = calcAverageTariff(filteredSites, summaryCurrencyCode);

  // Chart data

  const hasAnyTakeOrPaySite = filteredSites.some(
    (site) => site.tariffConfig.takeOrPay
  );
  const metrics = {
    totalSites: siteCount,
    totalAnnualDemand,
    averageTariff,
    isAnySitePvLease: isAnySitePvLease(filteredSites),
    totalEnergyProcuredCost,
    emissionsAvoided,
    totalPpaSavings,
    hideSavings: hasAnyTakeOrPaySite,
  };

  useEffect(() => {
    calcLandlordFrequency(sites);
  }, [sites]);

  useEffect(() => {
    saveSessionStoredValue(organisation.id, "landlord", selectedLandlordOrgIDs);
  }, [organisation, selectedLandlordOrgIDs]);

  // Sort and filter sites
  useEffect(() => {
    let listedSites = [...sites];
    if (selectedLandlordOrgIDs.length > 0 && landlordFilteringObject) {
      listedSites = listedSites.filter((site) =>
        selectedLandlordOrgIDs.some((orgID) =>
          landlordFilteringObject[orgID].siteIDs.includes(site.id)
        )
      );
    }

    // Text search based on name, address or tenant name
    if (textFilter.length > 0) {
      listedSites = filterSitesByText(sites, textFilter);
    }

    sortByColumn(listedSites, sortColumn, sortAscending, organisation);
    setFilteredSites(listedSites);
  }, [
    landlordFilteringObject,
    organisation,
    selectedLandlordOrgIDs,
    sites,
    sortAscending,
    sortColumn,
    textFilter,
  ]);

  const handleColumnClick = (columnKey: string) => {
    if (sortColumn === columnKey) {
      togglesortAscending(setSortAscending);
      return;
    }
    setSortColumn(columnKey);
    setSortAscending(sortAscendingForColumn(columnKey));
  };

  const tableHead = (
    <TenantSiteHeader
      displayInRangeScoreCol={displayInRangeScoreCol}
      allSitesNotStarted={allSitesNotStarted}
      sortColumn={sortColumn}
      sortAscending={sortAscending}
      handleColumnClick={handleColumnClick}
      hideSavings={hasAnyTakeOrPaySite}
    />
  );
  const tableRows = filteredSites.map((site) => (
    <TenantSiteRow
      key={site.id}
      site={site}
      organisation={organisation}
      displayInRangeScoreCol={displayInRangeScoreCol}
      allSitesNotStarted={allSitesNotStarted}
      hideSavings={hasAnyTakeOrPaySite}
    />
  ));

  const landlordSelectOptions = sortOwnershipSelectOptions(
    Object.entries(landlordFilteringObject).map(([orgID, filterObject]) => ({
      id: orgID,
      value: filterObject.siteIDs,
      label: filterObject.name,
    }))
  );

  return (
    <>
      <TenantSiteListMetricCards
        metrics={metrics}
        summaryCurrencyCode={summaryCurrencyCode}
      />
      <NoPaddedRow>
        <TableListSortFilters
          textFilter={textFilter}
          setTextFilter={setTextFilter}
          orgFiltering={{
            setSelectedOrgIDs: setSelectedLandlordOrgIDs,
            onOwnershipFiltering: onLandlordFiltering,
            selectedOrgIDs: selectedLandlordOrgIDs,
            ownershipSelectOptions: landlordSelectOptions,
            isLandlord: false,
          }}
        />
      </NoPaddedRow>
      <NoPaddedRow>
        <Table
          head={tableHead}
          rows={tableRows}
          id={"tenant-site-list-table"}
        />
      </NoPaddedRow>
    </>
  );
};

export default TenantSiteListView;
